import React from "react"
import { useState } from "react"
import * as styles from "./FaqSchemaSection.module.scss"
import { RiDeleteBinLine } from "react-icons/ri"
import LoadingSpinner from "../Loader/Loader"

type FaqSchemaProps = {
  title?: string
  subtitle?: string
  contentText?: string
  question?: string
  questionPlaceholder?: string
  answer?: string
  answerPlaceholder?: string
  generateButtonText?: string
  addButtonText?: string
  copyButtonText?: string
  outputBoxTitle?: string
  copiedButtonText?: string
  genrateSchemaText?: string
}

const FaqSchemaSection: React.FC<FaqSchemaProps> = ({
  title,
  subtitle,
  contentText,
  question,
  questionPlaceholder,
  answer,
  answerPlaceholder,
  generateButtonText,
  addButtonText,
  copyButtonText,
  outputBoxTitle,
  copiedButtonText,
  genrateSchemaText,
}) => {
  const [faqs, setFaqs] = useState([{ question: "", answer: "" }])
  const [schema, setSchema] = useState("")
  const [loading, setLoading] = useState(false)

  const addQuestion = () => {
    setFaqs([...faqs, { question: "", answer: "" }])
  }

  const updateFaq = (
    index: number,
    field: "question" | "answer",
    value: string
  ) => {
    const updated = [...faqs]
    updated[index][field] = value
    setFaqs(updated)
  }

  const deleteFaq = (index: number) => {
    const updated = faqs.filter((_, i) => i !== index)
    setFaqs(updated.length ? updated : [{ question: "", answer: "" }]) 
    setSchema("")
  }


  const generateCode = async () => {
    setLoading(true) 
    try {
      const response = await fetch("/.netlify/functions/faq-schema", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: faqs }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("❌ Netlify Function Error:", errorText)
        setSchema(`Error: ${errorText}`)
        return
      }

      const data = await response.json()

      setSchema(JSON.stringify(data, null, 2))
    } catch (error) {
      console.error("❌ Fetch Error:", error)
      setSchema("Error generating schema")
    } finally {
      setLoading(false) 
    }
  }

  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (schema) {
      navigator.clipboard.writeText(schema)
      setCopied(true)

      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <section className={`  c-padding ${styles.section}  `}>
      <div className={` ${styles.container} container`}>
        <h1 className={` ${styles.title} global-spacing-bottom`}>{title}</h1>
        <p className={` ${styles.subtitle}`}>{subtitle}</p>
        <h2 className="global-spacing">{contentText}</h2>

        <div className={styles.wrapper}>
          {/* Left: Form */}
          <div className={` ${styles.form} `}>
            {faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <div className={styles.faqHeader}>
                  <label className="">
                    {question}
                    {index + 1}
                  </label>
                 {/* <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => deleteFaq(index)}
                    aria-label={`Delete FAQ ${index + 1}`}
                  >
                    <RiDeleteBinLine />
                    <span className="sr_only">Delete FAQ {index + 1}</span>
                  </button> */}
                  {index > 0 && (
                    <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => deleteFaq(index)}
                        aria-label={`Delete FAQ ${index + 1}`}
                    >
                        <RiDeleteBinLine />
                        <span className="sr_only">Delete FAQ {index + 1}</span>
                    </button>
                    )}

                </div>
                <input
                  type="text"
                  placeholder={questionPlaceholder || "Add your question here"}
                  value={faq.question}
                  onChange={e => updateFaq(index, "question", e.target.value)}
                />
                <label className="global-spacing-top">
                  {answer}
                  {index + 1}
                </label>
                <input
                  type="text"
                  placeholder={answerPlaceholder || "Add your answer here"}
                  value={faq.answer}
                  onChange={e => updateFaq(index, "answer", e.target.value)}
                />
              </div>
            ))}

            <div className={`${styles.buttons} `}>
              <button
                onClick={addQuestion}
                className={`primaryOutline onPrimaryOutline ${styles.buttonQues}`}
              >
                {addButtonText || "Add Question"}
              </button>
              <button
                onClick={generateCode}
                className={`primaryFilled ${styles.btn}`}
              >
                {generateButtonText || "Generate Code"}
              </button>
            </div>
          </div>

          <div className={styles.codeBox}>
            <p>{outputBoxTitle}</p>
            <textarea readOnly placeholder={genrateSchemaText} value={schema} />

            <button
              className={`primaryFilled ${styles.copyButtonText}`}
              onClick={handleCopy}
              disabled={!schema}
            >
              {copied
                ? copiedButtonText
                : copyButtonText || "Copy to Clipboard"}
            </button>
          </div>
        </div>
      </div>
      {loading && (
        <div role="status" aria-live="polite" className={styles.loadingBox}>
          <LoadingSpinner overLay={true} />
          <span className="visually-hidden">Fetching results...</span>
        </div>
      )}
    </section>
  )
}

export default FaqSchemaSection
